import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateCanvas } from 'utils';
import styled from 'styled-components';
import constants from 'constants/index';
import _ from 'lodash';

const { CANVAS_UPDATE_TYPES, CANVAS_UPDATE_METHODS } = constants;

// ─── Icons ────────────────────────────────────────────────────────────────────

const PlayIcon = () => (
  <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M4 2.5v11l9-5.5-9-5.5z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="3" y="2" width="3.5" height="12" rx="1" />
    <rect x="9.5" y="2" width="3.5" height="12" rx="1" />
  </svg>
);

// ─── Styled Components ────────────────────────────────────────────────────────

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  min-height: 44px;
  padding: 0 10px 0 6px;
  border-radius: 999px;
  border: var(--pm-surface-border);
  background: var(--pm-card-bg);
  backdrop-filter: blur(14px);
`;

const Btn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid var(--pm-chip-border);
  background: var(--pm-chip-bg);
  color: var(--pm-text-secondary);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s, transform 0.1s;

  svg {
    width: 13px;
    height: 13px;
    fill: currentColor;
    display: block;
  }

  &:hover:not(:disabled) {
    border-color: var(--pm-accent-border);
    color: var(--pm-accent-text);
    background: var(--pm-accent-soft);
  }

  &:active:not(:disabled) {
    transform: scale(0.92);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const PlayBtn = styled(Btn)`
  width: 36px;
  height: 36px;

  svg {
    width: 15px;
    height: 15px;
  }
`;

const Track = styled.div`
  flex: 1;
  position: relative;
  height: 4px;
  min-width: 40px;
  border-radius: 999px;
  background: var(--pm-chip-border);
  cursor: pointer;
  align-self: center;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => Math.min(props.$progress * 100, 100)}%;
    border-radius: 999px;
    background: var(--pm-accent-text);
    pointer-events: none;
    transition: ${props => (props.$dragging ? 'none' : 'width 0.12s linear')};
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: ${props => Math.min(props.$progress * 100, 100)}%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border-radius: 999px;
    background: var(--pm-accent-text);
    box-shadow: 0 0 0 2.5px var(--pm-card-bg);
    pointer-events: none;
    opacity: ${props => (props.$thumbVisible ? 1 : 0)};
    transition: opacity 0.15s ease${props => (props.$dragging ? '' : ', left 0.12s linear')};
  }
`;

const RangeInput = styled.input.attrs({ type: 'range' })`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: 24px;
  margin: 0;
  padding: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
  -webkit-appearance: none;
`;

const Time = styled.span`
  flex-shrink: 0;
  color: var(--pm-text-muted);
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  white-space: nowrap;
  min-width: 32px;
`;

const SelectWrap = styled.div`
  flex-shrink: 0;
  position: relative;
  display: inline-flex;
  align-items: center;

  &::after {
    content: '';
    position: absolute;
    right: 11px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid var(--pm-text-muted);
    pointer-events: none;
  }
`;

const AnimSelect = styled.select`
  width: 148px;
  min-height: 32px;
  padding: 0 28px 0 12px;
  border-radius: 999px;
  border: 1px solid var(--pm-chip-border);
  background: var(--pm-chip-bg);
  color: var(--pm-chip-text);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  transition: border-color 0.15s, color 0.15s;
  color-scheme: ${props => props.$isDark ? 'dark' : 'light'};

  option {
    background: ${props => props.$isDark ? '#1e293b' : '#ffffff'};
    color: ${props => props.$isDark ? 'rgba(255,255,255,0.88)' : '#1e3a8a'};
  }

  &:hover {
    border-color: var(--pm-accent-border);
    color: var(--pm-accent-text);
  }

  &:focus {
    outline: none;
    border-color: var(--pm-accent-border);
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FALLBACK_DURATION = 10;

const fmt = secs => {
  if (!Number.isFinite(secs)) return '--';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// ─── Component ────────────────────────────────────────────────────────────────

const PreviewAnimations = () => {
  const canvasStore = useSelector(state => state.canvas);
  const serializedData = canvasStore.serializedData;
  const selectedTheme = useSelector(state => state.userData.userPreferences.theme);
  const isDark = selectedTheme !== constants.THEME.LIGHT;
  const selectedAnimation = serializedData?.metadata?.selectedAnimation;
  const animations = serializedData?.metadata?.animations || [];

  const canvasTime = serializedData?.metadata?.selectedAnimationTime ?? 0;
  const canvasProgress = serializedData?.metadata?.selectedAnimationProgress;
  const canvasDuration = serializedData?.metadata?.selectedAnimationDuration ?? null;
  const canvasIsPlaying = Boolean(serializedData?.metadata?.selectedAnimationIsPlaying);

  const dispatch = useDispatch();
  const autoSelectedKeyRef = useRef('');
  const autoPlayTimerRef = useRef(null);

  const [ isDragging, setIsDragging ] = useState(false);
  const [ thumbVisible, setThumbVisible ] = useState(false);
  const [ dragProgress, setDragProgress ] = useState(null);

  const updateArgs = useMemo(() => ({
    dispatch,
    type: CANVAS_UPDATE_TYPES.MESH,
    method: CANVAS_UPDATE_METHODS.UPDATE,
  }), [ dispatch ]);

  const currentAnim = useMemo(() => {
    if (selectedAnimation == null) return null;

    return animations.find((a, i) => {
      return String(a?.id ?? i) === String(selectedAnimation);
    }) ?? null;
  }, [ selectedAnimation, animations ]);

  const startAnimation = useCallback(animationId => {
    const nextId = String(animationId);

    setIsDragging(false);
    setDragProgress(null);

    updateCanvas(updateArgs, {
      animationId: nextId,
      loopAnimation: true,
      seekAnimation: true,
      animationProgress: 0,
      playAnimation: true,
    });
  }, [ updateArgs ]);

  useEffect(() => {
    if (_.isEmpty(animations)) {
      autoSelectedKeyRef.current = '';
      return;
    }

    const key = animations.map((a, i) => String(a?.id ?? i)).join(',');
    if (autoSelectedKeyRef.current === key) return;

    autoSelectedKeyRef.current = key;

    const hasValidSelection = selectedAnimation != null && animations.some((a, i) => {
      return String(a?.id ?? i) === String(selectedAnimation);
    });

    if (!hasValidSelection) {
      const firstVal = animations[0]?.id ?? '0';
      startAnimation(firstVal);
    } else {
      // The canvas already has a valid animation selected (it auto-started internally).
      // Send a delayed play command so the canvas begins streaming
      // selectedAnimationTime / selectedAnimationProgress into serializedData —
      // without it the timeline stays frozen at 0 even though canvasIsPlaying is true.
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = setTimeout(() => {
        updateCanvas(updateArgs, { playAnimation: true, loopAnimation: true });
      }, 200);
    }
  }, [ animations, selectedAnimation, startAnimation, updateArgs ]);

  useEffect(() => {
    return () => clearTimeout(autoPlayTimerRef.current);
  }, []);

  const duration = canvasDuration || currentAnim?.duration || FALLBACK_DURATION;
  const baseProgress = Number.isFinite(canvasProgress)
    ? Math.min(Math.max(canvasProgress, 0), 1)
    : (duration > 0 ? Math.min(canvasTime / duration, 1) : 0);

  const progress = isDragging && dragProgress != null ? dragProgress : baseProgress;
  const currentTime = isDragging && dragProgress != null ? dragProgress * duration : canvasTime;
  const hasSelection = !!currentAnim;

  const handlePlayPause = useCallback(() => {
    if (!hasSelection) return;

    updateCanvas(
      updateArgs,
      canvasIsPlaying
        ? { pauseAnimation: true }
        : { playAnimation: true, loopAnimation: true },
    );
  }, [ hasSelection, canvasIsPlaying, updateArgs ]);

  const handleScrub = useCallback(e => {
    const ratio = parseInt(e.target.value, 10) / 1000;
    setDragProgress(ratio);

    updateCanvas(updateArgs, {
      seekAnimation: true,
      animationProgress: ratio,
    });
  }, [ updateArgs ]);

  const finishScrub = useCallback(() => {
    setIsDragging(false);
    setDragProgress(null);
  }, []);

  if (_.isEmpty(animations)) {
    return null;
  }

  const firstVal = animations[0]?.id ?? '0';
  const timeLabel = duration != null
    ? `${fmt(currentTime)} / ${fmt(duration)}`
    : fmt(currentTime);

  return (
    <Bar>
      <PlayBtn
        type="button"
        disabled={!hasSelection}
        title={canvasIsPlaying ? 'Pause' : 'Play'}
        onClick={handlePlayPause}
      >
        {canvasIsPlaying ? <PauseIcon /> : <PlayIcon />}
      </PlayBtn>

      <Track
        $progress={progress}
        $dragging={isDragging}
        $thumbVisible={thumbVisible || isDragging}
        onMouseEnter={() => setThumbVisible(true)}
        onMouseLeave={() => setThumbVisible(false)}
      >
        <RangeInput
          min="0"
          max="1000"
          step="1"
          value={Math.round(progress * 1000)}
          disabled={!hasSelection}
          onChange={handleScrub}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={finishScrub}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={finishScrub}
        />
      </Track>

      <Time>{timeLabel}</Time>

      <SelectWrap>
        <AnimSelect
          $isDark={isDark}
          value={selectedAnimation || firstVal}
          onChange={e => startAnimation(e.target.value)}
          title="Select animation"
        >
          {animations.map((anim, i) => {
            const val = anim?.id ?? String(i);
            const label = anim?.label || anim?.name || `Animation ${i + 1}`;
            return (
              <option key={val} value={val}>{label}</option>
            );
          })}
        </AnimSelect>
      </SelectWrap>
    </Bar>
  );
};

export { PreviewAnimations };
